/**
 * Table Bubble Menu
 * 
 * Floating toolbar that appears when cursor is inside a table.
 * Provides controls for adding/removing rows and columns.
 */

import { BubbleMenu, Editor } from '@tiptap/react';

interface TableBubbleMenuProps {
  editor: Editor;
}

export function TableBubbleMenu({ editor }: TableBubbleMenuProps) {
  if (!editor) {
    return null;
  }
  
  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ 
        duration: 100,
        placement: 'top',
        offset: [0, 10],
      }}
      shouldShow={({ editor }) => {
        // Only show when cursor is inside a table
        return editor.isActive('table');
      }}
      className="bg-card border border-border rounded-lg shadow-lg p-1 flex items-center gap-0.5"
    >
      {/* Row operations */}
      <div className="flex items-center gap-0.5 border-r border-border pr-1 mr-1">
        <button
          onClick={() => editor.chain().focus().addRowBefore().run()}
          disabled={!editor.can().addRowBefore()}
          className="p-1.5 text-xs rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          title="Add row above"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="sr-only">Add row above</span>
        </button>
        
        <button
          onClick={() => editor.chain().focus().addRowAfter().run()}
          disabled={!editor.can().addRowAfter()}
          className="p-1.5 text-xs rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          title="Add row below"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="10" width="18" height="4" rx="1" />
            <path d="M12 17v4M12 3v4" />
          </svg>
          <span className="sr-only">Add row below</span>
        </button>
        
        <button
          onClick={() => editor.chain().focus().deleteRow().run()}
          disabled={!editor.can().deleteRow()}
          className="p-1.5 text-xs rounded hover:bg-destructive/10 text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
          title="Delete row"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="10" width="18" height="4" rx="1" />
            <path d="M8 12h8" />
          </svg>
          <span className="sr-only">Delete row</span>
        </button>
      </div>
      
      {/* Column operations */}
      <div className="flex items-center gap-0.5 border-r border-border pr-1 mr-1">
        <button
          onClick={() => editor.chain().focus().addColumnBefore().run()}
          disabled={!editor.can().addColumnBefore()}
          className="p-1.5 text-xs rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          title="Add column left"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="10" y="3" width="4" height="18" rx="1" />
            <path d="M3 12h4M17 12h4" />
          </svg>
          <span className="sr-only">Add column left</span>
        </button>
        
        <button
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          disabled={!editor.can().addColumnAfter()}
          className="p-1.5 text-xs rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          title="Add column right"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="10" y="3" width="4" height="18" rx="1" />
            <path d="M17 12h4M3 12h4" />
          </svg>
          <span className="sr-only">Add column right</span>
        </button>
        
        <button
          onClick={() => editor.chain().focus().deleteColumn().run()}
          disabled={!editor.can().deleteColumn()}
          className="p-1.5 text-xs rounded hover:bg-destructive/10 text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
          title="Delete column"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="10" y="3" width="4" height="18" rx="1" />
            <path d="M12 8v8" strokeLinecap="round" />
          </svg>
          <span className="sr-only">Delete column</span>
        </button>
      </div>
      
      {/* Table operations */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          className={`p-1.5 text-xs rounded hover:bg-muted ${
            editor.isActive('tableHeader') ? 'bg-primary/10' : ''
          }`}
          title="Toggle header row"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
          </svg>
          <span className="sr-only">Toggle header row</span>
        </button>
        
        <button
          onClick={() => editor.chain().focus().deleteTable().run()}
          disabled={!editor.can().deleteTable()}
          className="p-1.5 text-xs rounded hover:bg-destructive/10 text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
          title="Delete table"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
          <span className="sr-only">Delete table</span>
        </button>
      </div>
    </BubbleMenu>
  );
}
